import { useIntl } from 'react-intl';
import {
  CartActionType,
  CartProduct,
  useCartDispatch,
  useCartState,
} from 'public/context/CartContext';
import AmountSelector from 'ui/CartAmountSelector/CartAmountSelector';
import { FallbackImage } from 'ui/UploadImage/fallBack/FallBackImage';
import { ProductUnityPrice } from 'utils/productCardHelpers';
import { CartProductPrice } from 'utils/productTotalPriceHelper';
import { StyledCartProductCard } from './style';
import CartSelect from '../../public/components/Selects/CartSelect';
import useMarketType from '../../utils/useMarketType';
import useQueryApiClient from '../../utils/useQueryApiClient';
import { options } from 'config/config';

const DEFAULT_IMAGE = options.images.defaultProductImage;

export interface ICartProductCard {
  product: CartProduct;
  amountCart: number;
}

export const CartProductCard = ({
  product,
  amountCart = 0,
}: ICartProductCard) => {
  const intl = useIntl();
  const cartDispatch = useCartDispatch();
  const { isEmarket } = useMarketType();
  const { cartName } = useCartState();

  const { appendData: moveData } = useQueryApiClient({
    request: {
      url: `api/v2/cart/:id/move`,
      method: 'POST',
    },
    onSuccess: () => {
      if (product?.cartProduct?.id && product?.cartProduct?.farm_id) {
        cartDispatch({
          type: CartActionType.REMOVE_ITEM,
          productId: product.cartProduct.id,
          farmId: product.cartProduct.farm_id,
        });
      }
    },
  });

  const cartProductAmount = product?.cartProduct?.product_amounts?.find(
    (amount) => amount.type === product.cartType
  );

  const uniqueMarks = product?.cartProduct?.qualitymarks?.filter(
    (q, i, arr) =>
      arr.findIndex((x) => x.quality_mark_id === q.quality_mark_id) === i
  );

  const removeProductFromCart = () => {
    if (product?.cartProduct)
      cartDispatch({
        type: CartActionType.REMOVE_ITEM,
        productId: product.cartProduct.id,
        farmId: product.cartProduct.farm_id,
      });
  };

  const cardCover = (
    <FallbackImage
      src={
        product.cartProduct?.image_small
          ? product.cartProduct?.image_small
          : product.cartProduct?.farm?.logo
            ? product.cartProduct?.farm?.logo
            : DEFAULT_IMAGE
      }
      type={'ProductCard'}
    />
  );

  return (
    <div>
      <StyledCartProductCard cover={cardCover}>
        <div className={'left-side-container'}>
          <p className={'cart-product-title'}>{product?.cartProduct?.name}</p>
          {uniqueMarks?.length ? (
            <div className={'quality-marks-container'}>
              {uniqueMarks.map((qmark, index) => (
                <FallbackImage
                  className="quality-mark"
                  src={qmark.quality_mark?.logo ? qmark.quality_mark.logo : ''}
                  type={'notFound'}
                  preview={false}
                  key={index}
                />
              ))}
            </div>
          ) : null}
          <div className={'amount-selector-wrapper'}>
            <AmountSelector
              amountCart={amountCart}
              product={product.cartProduct}
              unitType={cartProductAmount?.storage_unit}
            />
          </div>
        </div>
        <div className={'right-side-container'}>
          <span
            onClick={removeProductFromCart}
            className={'remove-cart-product-btn'}
          >
            {intl.formatMessage({ id: 'general.delete' })}
          </span>
          <div className={'price-container'}>
            <CartProductPrice product={product} type={'TOTAL'} />
            <CartProductPrice product={product} type={'PER_UNIT'} />
            <ProductUnityPrice productAmount={cartProductAmount} />
          </div>
        </div>
      </StyledCartProductCard>
      {!isEmarket && !cartName && (
        <CartSelect
          label={intl.formatMessage({ id: 'cart.add_to_different_cart' })}
          layout="vertical"
          onChange={(value) => {
            moveData(
              {
                product: product,
              },
              { id: value }
            );
          }}
        />
      )}
    </div>
  );
};
